import React, { useState, useRef, useLayoutEffect } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDoubleDown,
  faChevronDoubleUp,
} from "@fortawesome/pro-solid-svg-icons";

import docs from "../../../../docs/directory.json";

import { Node } from "types/explain";

import { formatBytes } from "utils/format";
import Button from "components/Button";
import classNames from "classnames";

const SignificantFields: React.FunctionComponent<{
  node: Node;
}> = ({ node }) => {
  const nodeType = node["Node Type"] as keyof typeof docs.planNodes;
  if (!(nodeType in docs.planNodes)) {
    return null;
  }
  const nodeDocs = docs.planNodes[nodeType];
  const sigFields = nodeDocs.importantFields.filter(
    (f) => f in node,
  ) as (keyof Node)[];
  if (sigFields.length === 0) {
    return null;
  }
  return (
    <dl className="mb-0">
      {sigFields.map((f) => {
        const value = node[f];
        if (!isSignificantFieldValue(value)) {
          return null;
        }

        return <SignificantField key={f} label={f} value={value} />;
      })}
    </dl>
  );
};

type IncrementalSortGroupInfo = {
  "Group Count": number;
  "Sort Methods Used": string[];
  "Sort Space Memory": {
    "Peak Sort Space Used": number;
    "Average Sort Space Used": number;
  };
  "Sort Space Disk": {
    "Peak Sort Space Used": number;
    "Average Sort Space Used": number;
  };
};

function isIncrementalSortGroupInfo(
  value: any,
): value is IncrementalSortGroupInfo {
  return (
    typeof value === "object" &&
    ["Group Count", "Sort Methods Used", "Sort Space Memory"].every(
      (k) => k in value,
    )
  );
}

function isSignificantFieldValue(value: any): value is SignificantFieldValue {
  switch (typeof value) {
    case "boolean":
    case "string":
    case "number":
      return true;
    case "object":
      if (isIncrementalSortGroupInfo(value)) {
        return true;
      }
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return true;
        }
        const firstElem = value[0];
        return typeof firstElem === "string" || typeof firstElem === "number";
      }
      return false;
    default:
      return false;
  }
}

type SignificantFieldValue =
  | boolean
  | string
  | number
  | string[]
  | number[]
  | IncrementalSortGroupInfo;

const SignificantField: React.FunctionComponent<{
  label: string;
  value: SignificantFieldValue;
}> = ({ label, value }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ddRef = useRef(null);
  useLayoutEffect(() => {
    if (
      ddRef.current &&
      ddRef.current.offsetWidth < ddRef.current.scrollWidth
    ) {
      setOverflowing(true);
    }
  }, [ddRef]);

  const handleToggleExpanded = () => {
    setExpanded((exp) => !exp);
  };

  const formattedValue = formatSignificantField(value);

  return (
    <div className="flex">
      <dt className="text-[#606060] whitespace-nowrap leading-tight font-semibold font-mono pr-1 flex-grow-0 flex-shrink-0">
        {label}:
      </dt>
      <dd className="flex leading-tight font-mono basis-0 flex-grow flex-shrink min-w-0">
        <span
          ref={ddRef}
          className={classNames(
            "flex-grow flex-shrink min-w-0",
            expanded
              ? "whitespace-pre-wrap break-words"
              : "whitespace-nowrap overflow-hidden text-ellipsis",
          )}
          title={formattedValue}
        >
          {formattedValue}
        </span>
        {overflowing && (
          <Button
            bare
            title={expanded ? "show less" : "show more"}
            className="ml-2 inline-block self-start text-xs"
            variant="link"
            onClick={handleToggleExpanded}
          >
            <FontAwesomeIcon
              icon={expanded ? faChevronDoubleUp : faChevronDoubleDown}
            />
          </Button>
        )}
      </dd>
    </div>
  );
};

function formatSignificantField(value: SignificantFieldValue): string {
  if (typeof value === "boolean") {
    return String(value);
  }
  if (isIncrementalSortGroupInfo(value)) {
    const groupCount = value["Group Count"];
    const sortMethods = value["Sort Methods Used"];
    const avgMem =
      (value["Sort Space Memory"]["Average Sort Space Used"] ?? 0) * 1024;
    const peakMem =
      (value["Sort Space Memory"]["Peak Sort Space Used"] ?? 0) * 1024;

    const avgDisk =
      (value["Sort Space Disk"]?.["Average Sort Space Used"] ?? 0) * 1024;
    const peakDisk =
      (value["Sort Space Disk"]?.["Peak Sort Space Used"] ?? 0) * 1024;
    const diskStats = value["Sort Space Disk"]
      ? `, avg. disk: ${formatBytes(avgDisk)}, peak disk: ${formatBytes(
          peakDisk,
        )}`
      : "";
    return `${groupCount} groups, sort method(s): ${formatSignificantField(
      sortMethods,
    )}, avg. memory: ${formatBytes(avgMem)}, peak memory: ${formatBytes(
      peakMem,
    )}${diskStats}`;
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatSignificantField(item)).join(", ");
  }
  return String(value);
}

export default SignificantFields;
